import { AHButton, Box, useBoxStyle } from "arkh";
import { DateTime } from "luxon";
import React, { useCallback, useEffect, useState } from "react";
import { useMutation, graphql } from "react-relay";
import { ScrollView } from "react-native-gesture-handler";
import { StepController } from "../../../components/StepController";
import { Pager } from "../../../components/Pager";
import { KPIPropertiesComponent } from "../components/KPIPropertiesComponent";
import { KPICreateGoals } from "./KPICreateGoals";
import { KPICreateSections } from "./KPICreateSections";
import { KPICreateType } from "./KPICreateType";
import { KPIFillMode, KPIUnit } from "../Edit/KPIEditParamList";
import { KPITeamGroup } from "../Edit/KPITeamGroup";
import { KPIFrequencySelectorComponent } from "./KPIFrequencySelectorComponent";
import { produceScheduleRule } from "../components/produceScheduleRule";
import { KPICreateMutation } from "./__generated__/KPICreateMutation.graphql";
import {
  UserLoggedInNavigatorParamList,
  UserLoggedInNavigatorScreenProps,
} from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { KPIAdminCreateAdminTeam } from "./KPICreateAdminTeam";

import { useIntl } from "react-intl";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import cuid from "cuid";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { KPICreateParams } from "./KPICreateParams";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ModalTabletAwareKeyboardProvider } from "@/src/components/mobile/ModalTabletAwareKeyboardStickyView";

export const KPICreate = withWebModalStack(KPICreate_);
function KPICreate_({
  route: {
    params: {
      title = "",
      step = 0,
      isCustomKPI = false,
      unit = "CUSTOM",
      icon = undefined,
      fillMode = "current",
      teamGroups = [],
      goalDirection = "less",
      adminTeam = null,
      selectedDays = [0, 1, 2, 3, 4, 5, 6],
      goalsActive = false,
    },
  },
  navigation,
}: UserLoggedInNavigatorScreenProps<"KPICreate">) {
  const intl_tokens = useIntlTokens();
  const intl = useIntl();
  const [width, setWidth] = useState(1);

  useNavigationHeader({
    type: "CLOSE",
    onClose: () => {
      navigation.goBack();
    },

    headerTitle:
      step === 0
        ? intl.formatMessage({
            defaultMessage: "Create KPI",
            description: "Create KPI navigation title",
          })
        : title,
    defaultContentStyle: true,
  });

  const [createRoutine, loading] = useMutation<KPICreateMutation>(graphql`
    mutation KPICreateMutation($input: CreateRoutineInput!) {
      createRoutine(input: $input) {
        id
      }
    }
  `);

  const onCreateRoutine = useCallback(() => {
    const isWeekly = selectedDays.length === 1 && selectedDays[0] === 0;
    const sections: {
      id: string;
      teamId: string;
      title: string | null;
      goalValue: number;
    }[] = teamGroups.flatMap((teamGroup) => {
      if (teamGroup.subTeams.length > 0) {
        return teamGroup.subTeams.map(
          (subTeam) =>
            ({
              id: cuid(),
              teamId: teamGroup.teamId,
              title: subTeam.name,
              goalValue: subTeam.goalValue,
            }) as const
        );
      }
      return [
        {
          id: cuid(),
          teamId: teamGroup.teamId,
          title: null as string | null,
          goalValue: teamGroup.goalValue,
        },
      ];
    });

    createRoutine({
      onCompleted: navigation.goBack,
      variables: {
        input: {
          type: "KPI",
          teamId: adminTeam!.id,
          title,
          icon,
          startAt: DateTime.local()
            .minus({
              days: !isWeekly ? 7 : undefined,
              weeks: isWeekly ? 7 : undefined,
            })
            .toISO(),
          tasks: [{ number: { id: "task", title, unit } }],
          connections: sections.map((s, i) => {
            return {
              number: {
                sectionId: s.id,
                taskId: "task",
                lessThan:
                  goalsActive && goalDirection === "less" ? s.goalValue : null,
                greaterThan:
                  goalsActive && goalDirection === "greater"
                    ? s.goalValue
                    : null,
              },
            };
          }),
          sections: sections.map(({ goalValue, ...section }) => section),
          schedule: {
            rules: [produceScheduleRule(selectedDays, fillMode, "task")],
          },
        },
      },
    });
  }, [
    navigation,
    createRoutine,
    goalDirection,
    goalsActive,
    selectedDays,
    fillMode,
    adminTeam,
    icon,
    title,
    unit,
    teamGroups,
  ]);

  const onNext = useCallback(() => {
    if (step === 5) {
      onCreateRoutine();
    } else {
      navigation.setParams({
        step: step + 1,
      });
    }
  }, [navigation, onCreateRoutine, step]);
  const onPrev = useCallback(() => {
    if (step === 0) {
      navigation.setParams({
        isCustomKPI: false,
      });
      return;
    }
    if (step === 3) {
      navigation.setParams({
        goalsActive: false,
      });
    }
    navigation.setParams({
      step: step - 1,
    });
  }, [step, navigation]);

  const insets = useSafeAreaInsets();

  useEffect(() => {
    navigation.setOptions({ headerBackVisible: step === 0 });
  }, [navigation, step]);

  const setParam =
    <T extends keyof UserLoggedInNavigatorParamList["KPICreate"]>(key: T) =>
    (value: UserLoggedInNavigatorParamList["KPICreate"][T]) => {
      navigation.setParams({
        [key]: value,
      });
    };

  const setSelectedTeams = useCallback(
    (teams: KPITeamGroup[] | ((sections: KPITeamGroup[]) => KPITeamGroup[])) =>
      navigation.setParams({
        teamGroups:
          typeof teams === "function" ? teams(teamGroups) : teamGroups,
      }),
    [navigation, teamGroups]
  );

  return (
    <ModalTabletAwareKeyboardProvider>
      <Box style={{ flex: 1 }}>
        <Box
          onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
          style={{ flex: 1 }}
          insets={{ bottom: insets.bottom }}
        >
          <Pager
            pages={[
              <KPICreateType
                key="create-type"
                onNext={() => {
                  navigation.setParams({
                    step: 1,
                  });
                }}
                title={title}
                icon={icon}
                unit={unit}
                isCustomKPI={isCustomKPI}
                setTitle={setParam("title")}
                setIcon={setParam("icon")}
                setUnit={setParam("unit")}
                setCusomKPI={setParam("isCustomKPI")}
              />,
              <KPIFrequencySelectorComponent
                key="frequency-selector"
                selectedDays={selectedDays}
                fillMode={fillMode}
                onSetFillMode={setParam("fillMode")}
                onSetSelectedDays={setParam("selectedDays")}
              />,
              <KPICreateSections
                key="create-sections"
                params={{
                  adminTeam,
                  fillMode,
                  goalDirection,
                  goalsActive,
                  icon: icon!,
                  isCustomKPI,
                  selectedDays,
                  teamGroups,
                  step,
                  title,
                  unit,
                }}
                setSelectedTeams={setSelectedTeams}
              />,
              <KPICreateGoals
                key="create-goals"
                onSkip={onNext}
                goalsActive={goalsActive}
                setGoalsActive={setParam("goalsActive")}
                selectedTeams={teamGroups}
                setSelectedTeams={setSelectedTeams}
                goalDirection={goalDirection}
                setGoalDirection={setParam("goalDirection")}
              />,
              <KPIAdminCreateAdminTeam
                key="create-admin-team"
                onNext={(adminTeam) =>
                  navigation.setParams({
                    adminTeam,
                    step: step + 1,
                  })
                }
              />,
              <KPIAdminCreateProperties
                key="create-properties"
                fillMode={fillMode}
                name={title}
                adminTeam={adminTeam}
                selectedDays={selectedDays}
                unit={unit}
                goalDirection={goalDirection}
                hasGoal={goalsActive}
                teamGroups={teamGroups}
              />,
            ]}
            pageIndex={step}
            width={width}
          />
        </Box>
        {step === 3 && !goalsActive ? (
          <Box insets={insets} space="medium" color="surface">
            <AHButton
              title={intl_tokens.ActivateTargets}
              themeType="primary"
              onPress={() => {
                setParam("goalsActive")(true);
              }}
            />
            <Box spaceTop="medium" />
            <AHButton title={intl_tokens.Skip} onPress={onNext} />
            <Box spaceTop="medium" />
            <AHButton
              title={intl_tokens.Previous}
              themeType="plain"
              onPress={onPrev}
            />
          </Box>
        ) : null}
        {(step === 3 && !goalsActive) || (step === 0 && !isCustomKPI) ? null : (
          <StepController
            color="surface"
            disabled={step === 2 && teamGroups.length === 0}
            nextLoading={loading}
            create={step === 5}
            space="medium"
            insets={insets}
            onNext={
              step === 4 || (step === 3 && !goalsActive) ? undefined : onNext
            }
            onPrev={onPrev}
          />
        )}
      </Box>
    </ModalTabletAwareKeyboardProvider>
  );
}

function KPIAdminCreateProperties(props: {
  name: string;
  goalDirection: "greater" | "less";
  hasGoal: boolean;
  teamGroups: KPITeamGroup[];
  selectedDays: number[];
  adminTeam: KPICreateParams["adminTeam"];
  unit: KPIUnit;
  fillMode: KPIFillMode;
}) {
  const contentContainerStyle = useBoxStyle({
    space: "medium",
  });

  if (!props.adminTeam) {
    throw new Error("KPICreate team is null");
  }

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={contentContainerStyle}
    >
      <KPIPropertiesComponent
        title={props.name}
        unit={props.unit}
        fillMode={props.fillMode}
        hasGoal={props.hasGoal}
        goalDirection={props.goalDirection}
        teamGroup={props.teamGroups}
        byweekday={props.selectedDays}
        team={props.adminTeam}
        spaceHorizontal="none"
      />
    </ScrollView>
  );
}
